import React, {
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { Link } from 'react-router-dom';
import * as SignalR from '@microsoft/signalr';
import AppContext from '../../../Context/AppContext';
import requestAxios from '../../../util/requestAxios';
import configData from '../../../config';
import DailyStreakTrackerRing from '../../PortalV2/Steps/DailyStreakTrackerRing';
import ActionStepMenu from '../ActionStepMenu';
import { useCountUp } from '../useCountUp';
import './ActionTrackerCard.css';

const MAX_DAILY = 15;
/** Ring fills in thirds (same business rule as original 3-arc wheel) */
const DISPLAY_SEGMENTS = 3;

function mapNamedCompletedToday(list) {
    return (Array.isArray(list) ? list : [])
        .filter((a) => a && String(a.text || a.Text || '').trim())
        .map((a) => ({
            id: a.id ?? a.Id,
            text: a.text ?? a.Text,
            extraText: a.extraText ?? a.ExtraText,
            canBeRemoved:
                a.canBeRemoved !== false && a.CanBeRemoved !== false,
        }));
}

/** Read completed-today list from GetUserNotCompletedActions (camel or Pascal). */
function pickNamedCompletedToday(data) {
    if (!data || typeof data !== 'object') return null;
    if (Array.isArray(data.namedCompletedToday)) return data.namedCompletedToday;
    if (Array.isArray(data.NamedCompletedToday)) return data.NamedCompletedToday;
    if (Array.isArray(data.todayCompletedActions)) return data.todayCompletedActions;
    if (Array.isArray(data.TodayCompletedActions)) return data.TodayCompletedActions;
    return null;
}

function numberWithCommas(x) {
    if (x == null || x === '') return '0';
    return String(x).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

function CheckIcon() {
    return (
        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden>
            <polyline
                points="20 6 9 17 4 12"
                stroke="currentColor"
                strokeWidth="2.75"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function RitualCheckIcon() {
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
            <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="1.5"
                opacity="0.45"
            />
            <polyline
                points="8 12.5 11 15.5 16.5 9"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function StreakBoltIcon() {
    return (
        <svg
            className="dv2-tracker__helper-icon"
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden
        >
            <path
                d="M13 2L4 14h7l-1 8 10-13h-7l1-7z"
                fill="currentColor"
                opacity="0.9"
            />
        </svg>
    );
}

function AddActionComposer({
    busy,
    onCancel,
    onSave,
    initialValue = '',
    title = 'Add action',
    hint = 'For today',
}) {
    const [value, setValue] = useState(initialValue);
    const submit = () => {
        if (busy) return;
        const trimmed = String(value || '').trim();
        if (!trimmed) return;
        onSave(trimmed);
    };

    return (
        <div
            className="dv2-tracker__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="dv2-tracker-add-title"
        >
            <div className="dv2-tracker__modal-card dv2-tracker__modal-card--add">
                <h4 id="dv2-tracker-add-title">{title}</h4>
                {hint ? (
                    <p className="dv2-tracker__composer-hint">{hint}</p>
                ) : null}
                <textarea
                    id="dv2-tracker-add"
                    className="dv2-tracker__composer-input"
                    value={value}
                    placeholder="What will you do today?"
                    rows={4}
                    autoFocus
                    disabled={busy}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submit();
                        }
                        if (e.key === 'Escape') onCancel();
                    }}
                />
                <div className="dv2-tracker__modal-actions">
                    <button
                        type="button"
                        className="dv2-tracker__modal-cancel"
                        onClick={onCancel}
                        disabled={busy}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="dv2-tracker__modal-ok"
                        onClick={submit}
                        disabled={busy || !String(value || '').trim()}
                    >
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
}

const ActionTrackerCard = ({
    onProgressRefresh,
    onOpenTracker,
    isActive = true,
}) => {
    const { checkUserSession } = useContext(AppContext);
    const { token, userId } = checkUserSession();

    const [actions, setActions] = useState([]);
    const [completedToday, setCompletedToday] = useState([]);
    const [todayCount, setTodayCount] = useState(0);
    const [lifetimeSteps, setLifetimeSteps] = useState(0);
    const [globalSteps, setGlobalSteps] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [adding, setAdding] = useState(false);
    const [showComposer, setShowComposer] = useState(false);
    const [editAction, setEditAction] = useState(null);
    const [confirmId, setConfirmId] = useState(null);
    const [deleteId, setDeleteId] = useState(null);
    const [menuActionId, setMenuActionId] = useState(null);
    const [listHint, setListHint] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [limitBuzz, setLimitBuzz] = useState(false);
    const [pickBuzz, setPickBuzz] = useState(false);
    const [compactDashboard, setCompactDashboard] = useState(() =>
        typeof window !== 'undefined'
            ? window.matchMedia('(max-width: 1199px)').matches
            : false
    );
    const [showActionsSheet, setShowActionsSheet] = useState(false);
    const wasInactiveRef = useRef(false);
    const buzzTimerRef = useRef(null);
    const pickBuzzTimerRef = useRef(null);
    const menuRef = useRef(null);
    const listWrapRef = useRef(null);
    const listRef = useRef(null);
    const listFooterRef = useRef(null);
    const [footerPinned, setFooterPinned] = useState(false);
    const [listOverflowing, setListOverflowing] = useState(false);
    const displayGlobalSteps = useCountUp(globalSteps, {
        ready: !loading,
        duration: 1800,
    });

    const namedPending = actions.filter(
        (a) => a && String(a.text || '').trim() !== ''
    );

    const loadActions = useCallback(async ({ soft = false } = {}) => {
        if (!token || !userId) return;
        if (!soft) {
            setLoading(true);
            setErrorMsg('');
        }
        const { error, data } = await requestAxios({
            url: `Actions/GetUserNotCompletedActions/${userId}/0`,
            token,
        });
        if (!error && data) {
            const next = Array.isArray(data.actions) ? data.actions : [];
            setActions(next);
            setTodayCount(Number(data.todayActionsCount) || 0);
            const completedRaw = pickNamedCompletedToday(data);
            if (completedRaw) {
                const mapped = mapNamedCompletedToday(completedRaw);
                setCompletedToday((prev) => {
                    if (mapped.length > 0) return mapped;
                    // Soft reload with empty list: keep optimistic rows still not pending
                    if (soft) {
                        return prev.filter(
                            (c) => !next.some((a) => a.id === c.id)
                        );
                    }
                    return mapped;
                });
            } else {
                setCompletedToday((prev) =>
                    prev.filter((c) => !next.some((a) => a.id === c.id))
                );
            }
        } else if (!soft) {
            setErrorMsg("Could not load today's actions.");
        }
        if (!soft) setLoading(false);
    }, [token, userId]);

    const loadLifetime = useCallback(async () => {
        if (!token || !userId) return;
        const { error, data } = await requestAxios({
            url: `Dashboard/GetUserProgress/${userId}`,
            token,
        });
        if (!error && data) {
            setLifetimeSteps(Number(data.stepsTaken) || 0);
        }
    }, [token, userId]);

    const loadGlobal = useCallback(async () => {
        if (!token) return;
        const { error, data } = await requestAxios({
            url: 'Actions/GetGlobalActionStepsTaken',
            token,
        });
        if (!error && data != null) {
            setGlobalSteps(data);
        }
    }, [token]);

    useEffect(() => {
        loadActions();
        loadLifetime();
        loadGlobal();
    }, [loadActions, loadLifetime, loadGlobal]);

    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia('(max-width: 1199px)');
        const onChange = () => setCompactDashboard(mq.matches);
        onChange();
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else mq.removeListener(onChange);
        };
    }, []);

    useEffect(
        () => () => {
            if (buzzTimerRef.current) clearTimeout(buzzTimerRef.current);
            if (pickBuzzTimerRef.current) clearTimeout(pickBuzzTimerRef.current);
        },
        []
    );

    useEffect(() => {
        if (menuActionId == null) return undefined;
        const onPointerDown = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuActionId(null);
            }
        };
        document.addEventListener('mousedown', onPointerDown);
        document.addEventListener('touchstart', onPointerDown);
        return () => {
            document.removeEventListener('mousedown', onPointerDown);
            document.removeEventListener('touchstart', onPointerDown);
        };
    }, [menuActionId]);

    // Soft refresh when returning from View All Actions / other panels
    useEffect(() => {
        if (!isActive) {
            wasInactiveRef.current = true;
            return;
        }
        if (!wasInactiveRef.current) return;
        wasInactiveRef.current = false;
        loadActions({ soft: true });
        loadLifetime();
        loadGlobal();
    }, [isActive, loadActions, loadLifetime, loadGlobal]);

    useEffect(() => {
        if (!token) return undefined;
        const hubConnection = new SignalR.HubConnectionBuilder()
            .withAutomaticReconnect()
            .withUrl(`${configData.SERVER_URL}actionhub`)
            .build();
        hubConnection.start().catch((err) => console.log(err));
        hubConnection.on('GetGlobalStepsTaken', (steps) => {
            setGlobalSteps(steps);
        });
        return () => {
            hubConnection.stop().catch(() => {});
        };
    }, [token]);

    /** Instant local update — no full list reload */
    const applyLocalComplete = (justCompleted) => {
        if (justCompleted) {
            setCompletedToday((prev) => {
                if (prev.some((c) => c.id === justCompleted.id)) return prev;
                return [...prev, justCompleted];
            });
            setActions((prev) => prev.filter((a) => a.id !== justCompleted.id));
        }
        setTodayCount((c) => Math.min(MAX_DAILY, c + 1));
        setLifetimeSteps((s) => s + 1);
        setGlobalSteps((g) => (typeof g === 'number' ? g + 1 : g));
    };

    const runComplete = async (actionId) => {
        if (saving || todayCount >= MAX_DAILY) return;
        const action = actions.find((a) => a.id === actionId);
        if (!action) return;

        const snapshot = {
            actions,
            completedToday,
            todayCount,
            lifetimeSteps,
            globalSteps,
        };
        const completedRow = {
            id: action.id,
            text: action.text,
            extraText: action.extraText,
        };

        // Close modal + update UI immediately
        setConfirmId(null);
        setListHint(false);
        setErrorMsg('');
        applyLocalComplete(completedRow);
        setSaving(true);

        const { error } = await requestAxios({
            url: 'Actions/AsyncCompleteAction',
            method: 'put',
            token,
            data: { userId, id: actionId },
        });
        setSaving(false);

        if (error) {
            setActions(snapshot.actions);
            setCompletedToday(snapshot.completedToday);
            setTodayCount(snapshot.todayCount);
            setLifetimeSteps(snapshot.lifetimeSteps);
            setGlobalSteps(snapshot.globalSteps);
            setErrorMsg('Could not complete this action. Try again.');
            return;
        }

        await loadActions({ soft: true });
        if (onProgressRefresh) onProgressRefresh();
    };

    const runEmptyComplete = async () => {
        if (saving || todayCount >= MAX_DAILY) return;

        const snapshot = {
            todayCount,
            lifetimeSteps,
            globalSteps,
        };

        setListHint(false);
        setErrorMsg('');
        applyLocalComplete(null);
        setSaving(true);

        const { error } = await requestAxios({
            url: 'Actions/AsyncInsertEmptyCompletedAction',
            method: 'post',
            token,
            data: { userId, text: '' },
        });
        setSaving(false);

        if (error) {
            setTodayCount(snapshot.todayCount);
            setLifetimeSteps(snapshot.lifetimeSteps);
            setGlobalSteps(snapshot.globalSteps);
            setErrorMsg('Could not record an action step. Try again.');
            return;
        }

        if (onProgressRefresh) onProgressRefresh();
    };

    const addTodayAction = async (text) => {
        const trimmed = String(text || '').trim();
        if (!trimmed || adding || !token) return;

        const unlocked = new Date();
        unlocked.setHours(0, 0, 0, 0);
        const unlockedIso = unlocked.toISOString();
        const tempId = `temp-${Date.now()}`;
        const snapshot = actions;

        setShowComposer(false);
        setErrorMsg('');
        setActions((prev) => [
            ...prev,
            {
                id: tempId,
                text: trimmed,
                unlockedDate: unlockedIso,
                canBeCompleted: true,
            },
        ]);
        setAdding(true);

        const { error, data } = await requestAxios({
            url: 'Actions/UpdateInsertAction',
            method: 'post',
            token,
            data: {
                Id: 0,
                Text: trimmed,
                UnlockedDate: unlockedIso,
            },
        });
        setAdding(false);

        if (error) {
            setActions(snapshot);
            setErrorMsg('Could not save action. Try again.');
            setShowComposer(true);
            return;
        }

        if (Array.isArray(data?.actions)) {
            setActions(data.actions);
            if (data.todayActionsCount != null) {
                setTodayCount(Number(data.todayActionsCount) || 0);
            }
        } else {
            await loadActions({ soft: true });
        }

        if (onProgressRefresh) onProgressRefresh();
    };

    const saveEditAction = async (text) => {
        const trimmed = String(text || '').trim();
        if (!trimmed || !editAction || adding || !token) return;

        const unlocked = new Date();
        unlocked.setHours(0, 0, 0, 0);
        const unlockedIso = unlocked.toISOString();
        const actionId = editAction.id;
        const wasDone = Boolean(editAction.done);
        const actionsSnap = actions;
        const completedSnap = completedToday;

        setEditAction(null);
        setErrorMsg('');

        if (wasDone) {
            setCompletedToday((prev) =>
                prev.map((a) =>
                    a.id === actionId ? { ...a, text: trimmed } : a
                )
            );
        } else {
            setActions((prev) =>
                prev.map((a) =>
                    a.id === actionId ? { ...a, text: trimmed } : a
                )
            );
        }
        setAdding(true);

        const { error, data } = await requestAxios({
            url: 'Actions/UpdateInsertAction',
            method: 'post',
            token,
            data: {
                Id: actionId,
                Text: trimmed,
                UnlockedDate: unlockedIso,
            },
        });
        setAdding(false);

        if (error) {
            setActions(actionsSnap);
            setCompletedToday(completedSnap);
            setErrorMsg('Could not save action. Try again.');
            return;
        }

        if (Array.isArray(data?.actions) && !wasDone) {
            setActions(data.actions);
            if (data.todayActionsCount != null) {
                setTodayCount(Number(data.todayActionsCount) || 0);
            }
        } else if (!wasDone) {
            await loadActions({ soft: true });
        }

        if (onProgressRefresh) onProgressRefresh();
    };

    const runDelete = async (actionId) => {
        if (saving || actionId == null) return;
        const wasPending = actions.find((a) => a.id === actionId);
        const wasCompleted = completedToday.find((a) => a.id === actionId);
        if (!wasPending && !wasCompleted) {
            setDeleteId(null);
            return;
        }

        const snapshot = {
            actions,
            completedToday,
            todayCount,
            lifetimeSteps,
            globalSteps,
        };

        setDeleteId(null);
        setMenuActionId(null);
        setErrorMsg('');
        setActions((prev) => prev.filter((a) => a.id !== actionId));
        setCompletedToday((prev) => prev.filter((a) => a.id !== actionId));
        if (wasCompleted) {
            setTodayCount((c) => Math.max(0, c - 1));
            setLifetimeSteps((s) => Math.max(0, s - 1));
            setGlobalSteps((g) =>
                typeof g === 'number' ? Math.max(0, g - 1) : g
            );
        }
        setSaving(true);

        const { error } = await requestAxios({
            url: `Actions/${actionId}`,
            method: 'delete',
            token,
        });
        setSaving(false);

        if (error) {
            setActions(snapshot.actions);
            setCompletedToday(snapshot.completedToday);
            setTodayCount(snapshot.todayCount);
            setLifetimeSteps(snapshot.lifetimeSteps);
            setGlobalSteps(snapshot.globalSteps);
            setErrorMsg('Could not delete action. Try again.');
            return;
        }

        await loadActions({ soft: true });
        if (onProgressRefresh) onProgressRefresh();
    };

    const handlePush = () => {
        if (saving) return;

        if (todayCount >= MAX_DAILY) {
            setLimitBuzz(false);
            window.requestAnimationFrame(() => {
                setLimitBuzz(true);
                if (typeof navigator !== 'undefined' && navigator.vibrate) {
                    navigator.vibrate([28, 36, 28]);
                }
            });
            if (buzzTimerRef.current) clearTimeout(buzzTimerRef.current);
            buzzTimerRef.current = setTimeout(() => setLimitBuzz(false), 480);
            return;
        }

        if (namedPending.length === 0) {
            setListHint(false);
            runEmptyComplete();
            return;
        }

        // Named actions left — prompt the user to pick which one to complete
        setListHint(true);
        setPickBuzz(false);
        window.requestAnimationFrame(() => {
            setPickBuzz(true);
            if (typeof navigator !== 'undefined' && navigator.vibrate) {
                navigator.vibrate([18, 32, 18]);
            }
        });
        if (pickBuzzTimerRef.current) clearTimeout(pickBuzzTimerRef.current);
        pickBuzzTimerRef.current = setTimeout(() => setPickBuzz(false), 720);
        if (compactDashboard) {
            setShowActionsSheet(true);
        } else if (listWrapRef.current?.scrollIntoView) {
            listWrapRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
            });
        }
    };

    const handleToggle = (action) => {
        if (saving || todayCount >= MAX_DAILY) return;
        if (action.canBeCompleted === false) return;
        setListHint(false);
        setShowActionsSheet(false);
        setConfirmId(action.id);
    };

    const listRows = [
        ...completedToday.map((a) => ({ ...a, done: true })),
        ...namedPending.map((a) => ({ ...a, done: false })),
    ];

    const streakDone = Math.min(todayCount, DISPLAY_SEGMENTS);
    const streakEarned = streakDone >= DISPLAY_SEGMENTS;
    const atDailyLimit = todayCount >= MAX_DAILY;
    const showAddMore = !loading;
    const trackerDisabled = saving || atDailyLimit;
    const canPulse = !loading && !trackerDisabled && todayCount < MAX_DAILY;

    useLayoutEffect(() => {
        const wrap = listWrapRef.current;
        if (!wrap) {
            setFooterPinned(false);
            return undefined;
        }

        const gapOf = (el) => {
            const g = getComputedStyle(el).gap || getComputedStyle(el).rowGap || '0';
            return parseFloat(g) || 0;
        };

        /** Intrinsic left height (children + gaps) — ignores grid stretch. */
        const leftNaturalHeight = (left) => {
            const gap = gapOf(left);
            const kids = Array.from(left.children);
            if (!kids.length) return 0;
            const sum = kids.reduce((acc, child) => acc + child.offsetHeight, 0);
            return sum + gap * Math.max(0, kids.length - 1);
        };

        const measure = () => {
            const list = listRef.current;
            const footer = listFooterRef.current;
            const body = wrap.closest('.dv2-tracker__body');
            const left = body?.querySelector('.dv2-tracker__left');

            if (!list || listRows.length === 0) {
                if (wrap.style.minHeight) wrap.style.minHeight = '';
                setFooterPinned(false);
                setListOverflowing(false);
                return;
            }

            const distanceFromBottom =
                list.scrollHeight - list.clientHeight - list.scrollTop;
            const canScroll = list.scrollHeight > list.clientHeight + 2;
            // Soft fade only while more content sits below the fold.
            setListOverflowing(canScroll && distanceFromBottom > 8);

            const listLooksFull =
                list.scrollHeight >= 300 || listRows.length >= 5;

            if (!listLooksFull || !left || !footer) {
                if (wrap.style.minHeight) wrap.style.minHeight = '';
                setFooterPinned(false);
                return;
            }

            const target = Math.ceil(leftNaturalHeight(left));
            if (target <= 0) {
                if (wrap.style.minHeight) wrap.style.minHeight = '';
                setFooterPinned(false);
                return;
            }

            const prev = parseInt(wrap.style.minHeight, 10) || 0;
            // Avoid ResizeObserver feedback: only write when value actually changes.
            if (Math.abs(prev - target) > 1) {
                wrap.style.minHeight = `${target}px`;
            }
            setFooterPinned(true);
        };

        measure();

        // Observe LEFT CONTENT only — never the wrap we mutate (that caused infinite growth).
        const ro =
            typeof ResizeObserver !== 'undefined'
                ? new ResizeObserver(() => {
                      requestAnimationFrame(measure);
                  })
                : null;
        const body = wrap.closest('.dv2-tracker__body');
        const left = body?.querySelector('.dv2-tracker__left');
        if (left) {
            Array.from(left.children).forEach((child) => ro?.observe(child));
        }
        if (listRef.current) ro?.observe(listRef.current);
        const listEl = listRef.current;
        listEl?.addEventListener('scroll', measure, { passive: true });
        window.addEventListener('resize', measure);
        return () => {
            ro?.disconnect();
            listEl?.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
            wrap.style.minHeight = '';
        };
    }, [listRows.length, listHint, loading, showAddMore]);

    let trackerPulseClass = ' trackerButton--complete';
    if (canPulse && todayCount < DISPLAY_SEGMENTS) {
        trackerPulseClass = ' trackerButton--pulseStreak';
    } else if (canPulse && todayCount < MAX_DAILY) {
        trackerPulseClass = ' trackerButton--pulseExtra';
    } else if (atDailyLimit) {
        trackerPulseClass = ' trackerButton--limitReached';
    }

    let listLead = '';
    if (todayCount <= 0) {
        listLead = 'Tap the tracker to complete your first action.';
    } else if (todayCount === 1) {
        listLead = "Nice start. Two more actions to earn today's streak.";
    } else if (todayCount === 2) {
        listLead = 'Keep going. One more action completes your streak.';
    } else if (todayCount === 3) {
        listLead =
            "Today's streak earned. You can keep going with extra actions.";
    } else if (todayCount >= 4 && todayCount < MAX_DAILY) {
        listLead = `Great momentum. You can complete up to ${MAX_DAILY} actions today.`;
    } else {
        listLead = "Great work. You reached today's action limit.";
    }

    const openTrackerTab = (tab = 'tracker') => {
        if (typeof onOpenTracker === 'function') {
            onOpenTracker(tab);
        }
    };

    const openActionsSheet = () => {
        setShowActionsSheet(true);
        setErrorMsg('');
    };

    const ctaEl =
        compactDashboard ? (
            <button
                type="button"
                className="dv2-card-cta"
                onClick={openActionsSheet}
            >
                View Actions →
            </button>
        ) : typeof onOpenTracker === 'function' ? (
            <button
                type="button"
                className="dv2-card-cta"
                onClick={() => openTrackerTab('tracker')}
            >
                View All Actions →
            </button>
        ) : (
            <Link
                to="/dashboard-v2?tracker=1&tab=tracker"
                className="dv2-card-cta"
            >
                View All Actions →
            </Link>
        );

    const addMoreFooter = showAddMore ? (
        <div className="dv2-tracker__list-footer" ref={listFooterRef}>
            <button
                type="button"
                className="dv2-tracker__add-cta"
                onClick={() => {
                    setErrorMsg('');
                    setShowComposer(true);
                }}
                disabled={adding || saving}
            >
                + Add more
            </button>
        </div>
    ) : null;

    const cardStateClass = streakEarned
        ? ' is-streak-done'
        : streakDone > 0
          ? ' is-streak-partial'
          : ' is-streak-empty';

    return (
        <section
            className={`dv2-tracker dv2-glass${cardStateClass}${
                compactDashboard ? ' dv2-tracker--compact' : ''
            }`}
            data-streak={`${streakDone}/${DISPLAY_SEGMENTS}`}
        >
            <div className="dv2-card-head dv2-tracker__head">
                <div className="dv2-tracker__head-copy">
                    <h2 className="dv2-section-title">Action Tracker</h2>
                    <span className="dv2-section-meta">Your daily ritual</span>
                </div>
                {ctaEl}
            </div>

            <div className="dv2-tracker__body">
                <div
                    className={`dv2-tracker__left${
                        limitBuzz ? ' is-limit-buzz' : ''
                    }`}
                >
                    <div
                        className={`dv2-tracker__ring-wrap${trackerPulseClass}`}
                        title={
                            atDailyLimit
                                ? "Today's action limit reached"
                                : 'Mark an action step complete'
                        }
                    >
                        <DailyStreakTrackerRing
                            size="compact"
                            variant="dashboard"
                            totalActions={DISPLAY_SEGMENTS}
                            completedActions={streakDone}
                            label={String(todayCount)}
                            subLabel="Actions"
                            onTap={handlePush}
                            disabled={trackerDisabled}
                            keepInteractive
                            pulseKey={todayCount}
                            showAmbient={false}
                            showGlow={false}
                            showLogo={false}
                        />
                    </div>

                    <div className="dv2-tracker__momentum">
                        <span className="dv2-tracker__momentum-label">
                            Global Momentum
                        </span>
                        <strong className="dv2-tracker__momentum-value">
                            {numberWithCommas(displayGlobalSteps)}
                        </strong>
                        <span className="dv2-tracker__momentum-sub">
                            actions completed
                        </span>
                    </div>
                </div>

                {/* Compact summary (laptop / mobile dashboard card) */}
                <div className="dv2-tracker__compact-panel">
                    {listLead ? (
                        <p className="dv2-tracker__compact-lead" role="status">
                            {listLead}
                        </p>
                    ) : null}
                    <p className="dv2-tracker__compact-meta">
                        {namedPending.length > 0
                            ? `${namedPending.length} named action${
                                  namedPending.length === 1 ? '' : 's'
                              } ready`
                            : streakEarned
                              ? 'Daily streak earned'
                              : 'Tap the ring to complete an action'}
                    </p>
                    <div className="dv2-tracker__compact-actions">
                        <button
                            type="button"
                            className="dv2-tracker__compact-cta"
                            onClick={openActionsSheet}
                        >
                            {namedPending.length > 0
                                ? 'View Actions'
                                : 'Manage Actions'}
                        </button>
                        {typeof onOpenTracker === 'function' ? (
                            <button
                                type="button"
                                className="dv2-tracker__compact-link"
                                onClick={() => openTrackerTab('tracker')}
                            >
                                Open full tracker →
                            </button>
                        ) : null}
                    </div>
                </div>

                <div
                    ref={listWrapRef}
                    className={`dv2-tracker__list-wrap${
                        footerPinned ? ' is-footer-pinned' : ''
                    }${pickBuzz ? ' is-pick-buzz' : ''}${
                        listHint ? ' is-pick-hint' : ''
                    }`}
                >
                    <div className="dv2-tracker__list-head">
                        <div className="dv2-tracker__list-head-copy">
                            <h3 className="dv2-tracker__list-title">
                                Today&apos;s Actions
                            </h3>
                            {listLead ? (
                                <p
                                    key={todayCount}
                                    className="dv2-tracker__list-lead"
                                    role="status"
                                    aria-live="polite"
                                >
                                    {listLead}
                                </p>
                            ) : null}
                        </div>
                    </div>

                    {listHint && namedPending.length > 0 && (
                        <div
                            className={`dv2-tracker__hint${
                                pickBuzz ? ' is-buzz' : ''
                            }`}
                            role="status"
                        >
                            <StreakBoltIcon />
                            <span>
                                Select an action below to mark it complete.
                            </span>
                        </div>
                    )}

                    {loading && (
                        <p className="dv2-tracker__status">Loading actions…</p>
                    )}

                    {!loading && listRows.length === 0 && (
                        <div
                            className={`dv2-tracker__empty${
                                streakEarned
                                    ? ' dv2-tracker__empty--complete'
                                    : ''
                            }`}
                        >
                            <div
                                className="dv2-tracker__empty-icon"
                                aria-hidden
                            >
                                {streakEarned ? (
                                    <RitualCheckIcon />
                                ) : (
                                    <StreakBoltIcon />
                                )}
                            </div>
                            <p className="dv2-tracker__empty-title">
                                {streakEarned
                                    ? 'Your daily ritual is complete'
                                    : 'Ready when you are'}
                            </p>
                            <p className="dv2-tracker__empty-copy">
                                No more named actions for today. Add a new
                                action or tap the tracker to keep building
                                momentum.
                            </p>
                            {addMoreFooter}
                        </div>
                    )}

                    {!loading && listRows.length > 0 && (
                        <>
                            <div
                                className={`dv2-tracker__list-shell${
                                    listOverflowing ? ' is-overflowing' : ''
                                }`}
                            >
                                <ul
                                    className="dv2-tracker__list"
                                    ref={listRef}
                                >
                                {listRows.map((action, index) => {
                                    const canClick =
                                        !action.done &&
                                        !saving &&
                                        todayCount < MAX_DAILY &&
                                        action.canBeCompleted !== false;
                                    const canComplete =
                                        !action.done &&
                                        !saving &&
                                        todayCount < MAX_DAILY &&
                                        action.canBeCompleted !== false;
                                    const canRemove =
                                        action.canBeRemoved !== false;
                                    const menuOpen =
                                        menuActionId === action.id;
                                    const menuOpensUp =
                                        index >= listRows.length - 2;
                                    return (
                                        <li
                                            key={action.id}
                                            className={[
                                                'dv2-tracker__item',
                                                action.done
                                                    ? 'dv2-tracker__item--done'
                                                    : 'dv2-tracker__item--pending',
                                                canClick
                                                    ? 'dv2-tracker__item--clickable'
                                                    : '',
                                                !action.done && pickBuzz
                                                    ? 'is-pick-buzz'
                                                    : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            onClick={
                                                canClick
                                                    ? () =>
                                                          handleToggle(action)
                                                    : undefined
                                            }
                                            onKeyDown={
                                                canClick
                                                    ? (e) => {
                                                          if (
                                                              e.key ===
                                                                  'Enter' ||
                                                              e.key === ' '
                                                          ) {
                                                              e.preventDefault();
                                                              handleToggle(
                                                                  action
                                                              );
                                                          }
                                                      }
                                                    : undefined
                                            }
                                            role={
                                                canClick ? 'button' : undefined
                                            }
                                            tabIndex={canClick ? 0 : undefined}
                                            aria-label={
                                                canClick
                                                    ? `Complete: ${action.text}`
                                                    : undefined
                                            }
                                        >
                                            {action.done ? (
                                                <span
                                                    className="dv2-tracker__check dv2-tracker__check--done"
                                                    aria-hidden
                                                >
                                                    <CheckIcon />
                                                </span>
                                            ) : (
                                                <span
                                                    className={`dv2-tracker__check${
                                                        !canClick
                                                            ? ' is-disabled'
                                                            : ''
                                                    }`}
                                                    aria-hidden
                                                />
                                            )}
                                            <span className="dv2-tracker__text">
                                                {action.text || 'Action step'}
                                                {action.extraText ? (
                                                    <em className="dv2-tracker__extra">
                                                        {' '}
                                                        {action.extraText}
                                                    </em>
                                                ) : null}
                                            </span>
                                            <ActionStepMenu
                                                open={menuOpen}
                                                opensUp={menuOpensUp}
                                                menuRef={menuRef}
                                                label={
                                                    action.text || 'action'
                                                }
                                                onToggle={() =>
                                                    setMenuActionId(
                                                        menuOpen
                                                            ? null
                                                            : action.id
                                                    )
                                                }
                                                showComplete={canComplete}
                                                onComplete={() => {
                                                    setMenuActionId(null);
                                                    handleToggle(action);
                                                }}
                                                onEdit={() => {
                                                    setMenuActionId(null);
                                                    setEditAction(action);
                                                }}
                                                showRemove={canRemove}
                                                onRemove={() => {
                                                    setMenuActionId(null);
                                                    setDeleteId(action.id);
                                                }}
                                            />
                                        </li>
                                    );
                                })}
                                </ul>
                                <div
                                    className="dv2-tracker__list-fade"
                                    aria-hidden="true"
                                />
                            </div>
                            {addMoreFooter}
                        </>
                    )}

                    {errorMsg && (
                        <p className="dv2-tracker__error">{errorMsg}</p>
                    )}
                </div>
            </div>

            {showActionsSheet && compactDashboard ? (
                <div
                    className="dv2-tracker__sheet"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="dv2-tracker-sheet-title"
                >
                    <button
                        type="button"
                        className="dv2-tracker__sheet-backdrop"
                        aria-label="Close actions"
                        onClick={() => setShowActionsSheet(false)}
                    />
                    <div
                        className={`dv2-tracker__sheet-panel${
                            pickBuzz ? ' is-pick-buzz' : ''
                        }${listHint ? ' is-pick-hint' : ''}`}
                    >
                        <div className="dv2-tracker__sheet-head">
                            <div>
                                <h3 id="dv2-tracker-sheet-title">
                                    Today&apos;s Actions
                                </h3>
                                {listLead ? (
                                    <p className="dv2-tracker__sheet-lead">
                                        {listLead}
                                    </p>
                                ) : null}
                            </div>
                            <button
                                type="button"
                                className="dv2-tracker__sheet-close"
                                onClick={() => setShowActionsSheet(false)}
                            >
                                Close
                            </button>
                        </div>
                        {listHint && namedPending.length > 0 ? (
                            <div
                                className={`dv2-tracker__hint${
                                    pickBuzz ? ' is-buzz' : ''
                                }`}
                                role="status"
                            >
                                <StreakBoltIcon />
                                <span>
                                    Select an action below to mark it complete.
                                </span>
                            </div>
                        ) : null}
                        {loading ? (
                            <p className="dv2-tracker__status">
                                Loading actions…
                            </p>
                        ) : null}
                        {!loading && listRows.length === 0 ? (
                            <div className="dv2-tracker__empty">
                                <p className="dv2-tracker__empty-title">
                                    No named actions yet
                                </p>
                                <p className="dv2-tracker__empty-copy">
                                    Add an action or tap the tracker ring to
                                    keep building momentum.
                                </p>
                            </div>
                        ) : null}
                        {!loading && listRows.length > 0 ? (
                            <ul className="dv2-tracker__sheet-list">
                                {listRows.map((action) => {
                                    const canClick =
                                        !action.done &&
                                        !saving &&
                                        todayCount < MAX_DAILY &&
                                        action.canBeCompleted !== false;
                                    return (
                                        <li
                                            key={`sheet-${action.id}`}
                                            className={[
                                                'dv2-tracker__item',
                                                action.done
                                                    ? 'dv2-tracker__item--done'
                                                    : 'dv2-tracker__item--pending',
                                                canClick
                                                    ? 'dv2-tracker__item--clickable'
                                                    : '',
                                                !action.done && pickBuzz
                                                    ? 'is-pick-buzz'
                                                    : '',
                                            ]
                                                .filter(Boolean)
                                                .join(' ')}
                                            onClick={
                                                canClick
                                                    ? () =>
                                                          handleToggle(action)
                                                    : undefined
                                            }
                                            role={
                                                canClick ? 'button' : undefined
                                            }
                                            tabIndex={canClick ? 0 : undefined}
                                        >
                                            {action.done ? (
                                                <span
                                                    className="dv2-tracker__check dv2-tracker__check--done"
                                                    aria-hidden
                                                >
                                                    <CheckIcon />
                                                </span>
                                            ) : (
                                                <span
                                                    className="dv2-tracker__check"
                                                    aria-hidden
                                                />
                                            )}
                                            <span className="dv2-tracker__text">
                                                {action.text || 'Action step'}
                                            </span>
                                        </li>
                                    );
                                })}
                            </ul>
                        ) : null}
                        <div className="dv2-tracker__sheet-footer">
                            <button
                                type="button"
                                className="dv2-tracker__add-cta"
                                onClick={() => {
                                    setShowActionsSheet(false);
                                    setShowComposer(true);
                                }}
                                disabled={adding || saving}
                            >
                                + Add more
                            </button>
                            {typeof onOpenTracker === 'function' ? (
                                <button
                                    type="button"
                                    className="dv2-tracker__compact-link"
                                    onClick={() => {
                                        setShowActionsSheet(false);
                                        openTrackerTab('tracker');
                                    }}
                                >
                                    Open full tracker →
                                </button>
                            ) : null}
                        </div>
                    </div>
                </div>
            ) : null}

            {showComposer ? (
                <AddActionComposer
                    busy={adding}
                    onCancel={() => setShowComposer(false)}
                    onSave={addTodayAction}
                />
            ) : null}

            {editAction ? (
                <AddActionComposer
                    key={`edit-${editAction.id}`}
                    busy={adding}
                    initialValue={editAction.text || ''}
                    title="Edit action"
                    hint={editAction.done ? 'Completed action' : 'For today'}
                    onCancel={() => setEditAction(null)}
                    onSave={saveEditAction}
                />
            ) : null}

            {confirmId != null && (
                <div className="dv2-tracker__modal" role="dialog" aria-modal="true">
                    <div className="dv2-tracker__modal-card">
                        <h4>Are you sure?</h4>
                        <p>Do you really want to complete this action?</p>
                        <div className="dv2-tracker__modal-actions">
                            <button
                                type="button"
                                className="dv2-tracker__modal-cancel"
                                onClick={() => setConfirmId(null)}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="dv2-tracker__modal-ok"
                                onClick={() => runComplete(confirmId)}
                                disabled={saving}
                            >
                                Complete
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {deleteId != null && (
                <div className="dv2-tracker__modal" role="dialog" aria-modal="true">
                    <div className="dv2-tracker__modal-card">
                        <h4>Delete action?</h4>
                        <p>This action will be removed from your list.</p>
                        <div className="dv2-tracker__modal-actions">
                            <button
                                type="button"
                                className="dv2-tracker__modal-cancel"
                                onClick={() => setDeleteId(null)}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="dv2-tracker__modal-ok"
                                onClick={() => runDelete(deleteId)}
                                disabled={saving}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default ActionTrackerCard;
